import { useState, type ComponentType } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  type SvgIconProps,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import RocketLaunchRounded from '@mui/icons-material/RocketLaunchRounded';
import ScienceRounded from '@mui/icons-material/ScienceRounded';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useAuth } from '@/auth';
import { AuroraBackground } from '@/components/AuroraBackground';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { useAsync } from '@/hooks/useAsync';
import { getCatalog } from '@/platform/billing/api';
import { savePlanIntent } from '@/platform/billing/labels';
import { DEFAULT_CHOICE } from '@/platform/billing/choice';
import { PlanPicker, QuoteSummary } from '@/platform/billing/PlanPicker';
import type { Catalog, FeatureKey, PlanChoice } from '@/platform/billing/types';
import { IndependenceNote } from '@/components/IndependenceNote';

const FAQ = [
  {
    q: 'Como funciona o período de teste?',
    a: 'Depois do cadastro a instituição usa tudo do plano escolhido, sem pagar nada, durante o teste. A primeira fatura vence no último dia; se ela não for paga, o acesso termina alguns dias depois. Nada é cobrado automaticamente no cartão sem você escolher pagar assim.',
  },
  {
    q: 'Quem pode cadastrar a instituição?',
    a: 'Quem responde pela IES junto ao Lousa: coordenação de curso, direção acadêmica, pró-reitoria. Pedimos o CNPJ, o código e-MEC, o endereço e os domínios de e-mail de estudantes e professores. Quem cadastra vira a coordenação da instituição na plataforma.',
  },
  {
    q: 'Como os estudantes entram?',
    a: 'Com a conta Google e o e-mail institucional, no domínio que a instituição informou (por exemplo, nome@aluno.suaies.edu.br). Professores pedem acesso e a coordenação aprova.',
  },
  {
    q: 'Quem revisa as questões dos simulados ENADE?',
    a: 'Professores do ensino superior das áreas de cada questão: Formação Geral e o componente específico de cada curso. Elas são autorais e seguem o formato do ENADE, com questões objetivas e discursivas, gabarito e resolução comentada. O Lousa não tem vínculo com o Inep nem com o MEC.',
  },
  {
    q: 'E se a instituição crescer no meio do contrato?',
    a: 'A coordenação muda o número de estudantes ou o plano a qualquer momento em “Plano e pagamentos”. O tempo já pago continua valendo e o novo valor entra na próxima fatura.',
  },
  {
    q: 'Posso cancelar?',
    a: 'Quando quiser, sem multa. As cobranças param e o acesso continua até o fim do período já pago.',
  },
];

const PROMISES: { icon: ComponentType<SvgIconProps>; text: string }[] = [
  { icon: ScienceRounded, text: 'dias grátis para testar' },
  { icon: PaymentsRounded, text: 'PIX, boleto ou cartão' },
  { icon: EventAvailableRounded, text: 'Cancele quando quiser' },
];

/** Public: plans, a price calculator and the way in for a new institution. */
export function PlansPage() {
  const catalog = useAsync(getCatalog);
  const { status } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState<PlanChoice>(DEFAULT_CHOICE);

  const start = () => {
    savePlanIntent(choice);
    void navigate('/instituicoes/nova');
  };

  return (
    <Box sx={{ minHeight: '100dvh' }}>
      <AuroraBackground />
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 5 },
          py: 1.5,
        }}
      >
        <RouterLink to="/" style={{ color: 'inherit', textDecoration: 'none' }} aria-label="Início">
          <BrandLogo size="medium" />
        </RouterLink>
        <Box sx={{ flex: 1 }} />
        <ColorModeToggle />
        <Button
          component={RouterLink}
          to={status === 'authenticated' ? '/' : '/login'}
          variant="tonal"
        >
          {status === 'authenticated' ? 'Abrir o Lousa' : 'Entrar'}
        </Button>
      </Stack>

      <Box component="main" sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pb: 8 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 780, mx: 'auto', pt: { xs: 3, md: 6 }, pb: 4 }}>
          <Chip
            size="small"
            variant="soft"
            color="lime"
            icon={<RocketLaunchRounded />}
            label="Para instituições de ensino superior"
            sx={{ mb: 2 }}
          />
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariationSettings: "'ROND' 100",
            }}
          >
            Um plano do tamanho da sua{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              instituição.
            </Box>
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.125rem' } }}
          >
            Pague por estudante, só pelos recursos que vai usar. Simulados no formato ENADE, com
            questões revisadas por professores do ensino superior de cada área, correção automática
            e relatórios que mostram o que cada turma precisa reforçar.
          </Typography>
          <Stack
            direction="row"
            useFlexGap
            sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 3 }}
          >
            {PROMISES.map(({ icon: Icon, text }, i) => (
              <Chip
                key={text}
                icon={<Icon />}
                variant="outlined"
                label={i === 0 && catalog.data ? `${catalog.data.trialDays} ${text}` : text}
              />
            ))}
          </Stack>
        </Box>

        {catalog.error ? (
          <Alert severity="error">
            Não foi possível carregar os planos. Tente de novo em instantes.
          </Alert>
        ) : !catalog.data ? (
          <Skeleton variant="rounded" height={420} />
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
                gap: 3,
                alignItems: 'start',
              }}
            >
              <PlanPicker
                catalog={catalog.data}
                value={choice}
                onChange={setChoice}
                dense={false}
              />
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
                <QuoteSummary
                  choice={choice}
                  trialDays={catalog.data.trialDays}
                  footer={
                    <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardRounded />}
                        onClick={start}
                      >
                        Cadastrar minha instituição
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center' }}
                      >
                        Você entra com a conta Google de quem responde pela instituição.
                      </Typography>
                    </Stack>
                  }
                />
              </Box>
            </Box>

            <Comparison catalog={catalog.data} />
          </>
        )}

        <Box sx={{ maxWidth: 820, mx: 'auto', mt: 8 }}>
          <Typography variant="h5" component="h2" sx={{ textAlign: 'center', mb: 3 }}>
            Perguntas frequentes
          </Typography>
          {FAQ.map((item) => (
            <Accordion key={item.q} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Card
          sx={(t) => ({
            mt: 8,
            p: { xs: 3, md: 5 },
            borderRadius: 6,
            textAlign: 'center',
            bgcolor: t.vars.palette.primary.main,
            color: t.vars.palette.primary.contrastText,
            border: 'none',
          })}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
            Sua instituição já usa o Lousa?
          </Typography>
          <Typography sx={{ mt: 1, opacity: 0.85 }}>
            Estudantes e professores só precisam entrar com a conta Google e o e-mail institucional.
          </Typography>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            color="lime"
            size="large"
            sx={{ mt: 3 }}
          >
            Entrar
          </Button>
        </Card>
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ textAlign: 'center', mt: 4 }}
        >
          Valores em reais, por ciclo. Pagamentos processados pelo Asaas. ·{' '}
          <Link href="/privacidade.html" color="inherit">
            Privacidade
          </Link>
        </Typography>
        <IndependenceNote sx={{ mt: 1 }} />
      </Box>
    </Box>
  );
}

const FEATURE_ROWS: { key: FeatureKey | 'coordinators'; label: string }[] = [
  {
    key: 'assessments',
    label: 'Simulados, banco de questões, correção automática e relatórios da avaliação',
  },
  { key: 'advanced_reports', label: 'Relatório individual de cada estudante' },
  { key: 'exports', label: 'Exportação de resultados e análise de itens' },
  {
    key: 'enade_content',
    label: 'Simulados ENADE prontos, revisados por professores de cada área',
  },
  { key: 'onboarding', label: 'Implantação assistida' },
  { key: 'priority_support', label: 'Suporte prioritário' },
  { key: 'coordinators', label: 'Contas de coordenação' },
];

/** Plans × features, with what each add-on can bring. */
function Comparison({ catalog }: { catalog: Catalog }) {
  return (
    <Box sx={{ mt: 8 }}>
      <Typography variant="h5" component="h2" sx={{ textAlign: 'center', mb: 3 }}>
        Compare os planos
      </Typography>
      <TableContainer component={Card} sx={{ borderRadius: 5 }}>
        <Table size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              {catalog.plans.map((p) => (
                <TableCell key={p.key} align="center" sx={{ fontWeight: 700 }}>
                  {p.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Only what the API offers now (paused services, e.g. priority support, stay out). */}
            {FEATURE_ROWS.filter(
              (row) => row.key === 'coordinators' || row.key in catalog.features,
            ).map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                {catalog.plans.map((p) => {
                  if (row.key === 'coordinators') {
                    return (
                      <TableCell key={p.key} align="center">
                        {p.coordinators ?? 'Ilimitadas'}
                      </TableCell>
                    );
                  }
                  const included = p.features.includes(row.key);
                  const addon = catalog.addons.find(
                    (a) =>
                      a.plans.includes(p.key) &&
                      ADDON_FEATURES[a.key]?.includes(row.key as FeatureKey),
                  );
                  return (
                    <TableCell key={p.key} align="center">
                      {included ? (
                        <CheckRounded color="success" aria-label="incluído" />
                      ) : addon ? (
                        <Chip size="small" variant="soft" label="complemento" />
                      ) : (
                        <RemoveRounded color="disabled" aria-label="não incluído" />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// What each add-on brings (mirrors ADDONS in back/app/platform/plans.py).
const ADDON_FEATURES: Record<string, FeatureKey[]> = {
  relatorios_avancados: ['advanced_reports', 'exports'],
  banco_enade: ['enade_content'],
  suporte_prioritario: ['priority_support'],
  implantacao: ['onboarding'],
  simulados_enem: ['enem'],
};
